/**
 * Rechts: twee losse vlakken, klant (wit, 42 procent) boven en copiloot
 * (lichtpaars, 58 procent) onder, met ruimte ertussen (ontwerp 26).
 * Elk vlak scrolt apart; de kop van elk vlak staat stil.
 *
 * De zaak komt uit dezelfde query als het midden (`v6Sleutels.zaak`),
 * dus één verzoek voor beide.
 *
 * Sessie 75: het klantvlak hangt niet meer per zaak (geen key). Tot de
 * zaak binnen is staat de zaak uit de lijstkaart in beeld, met de
 * klantkaart van een eerdere zaak van dezelfde klant; klant en orders
 * hangen aan het klant-id en laden bij een zaak van dezelfde klant niet
 * opnieuw. De copiloot begint wel per zaak leeg (ontwerp).
 *
 * iPhone (onder 900 px): de kolom is weg. Kiest het midden Klant of
 * Copiloot (`useV6().openRechts`), dan schuift alleen dat vlak omhoog
 * over het gesprek, met een kruisje rechtsboven. Een tik ernaast sluit.
 */
import { zaakUitKaart } from "../zaakUitKaart";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { v6Api, type V6Zaak } from "../api";
import { useV6 } from "../context";
import { kaartUitCache } from "../naActie";
import { v6Sleutels } from "../sleutels";
import { Copilootvlak } from "./Copilootvlak";
import { Klantvlak } from "./Klantvlak";
import "./rechts.css";

const SMAL = "(max-width: 900px)";

function isSmal(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia(SMAL).matches
    : false;
}

/** Staat het scherm in de iPhone-indeling? Volgt draaien en slepen. */
function useSmal(): boolean {
  const [smal, setSmal] = useState(isSmal);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const lijst = window.matchMedia(SMAL);
    const bij = () => setSmal(lijst.matches);
    lijst.addEventListener?.("change", bij);
    return () => lijst.removeEventListener?.("change", bij);
  }, []);
  return smal;
}

/** De klantkaart uit een zaak van dezelfde klant die al in de cache staat. */
function klantkaartVanKlant(qc: QueryClient, klantId: string | null): V6Zaak["klantkaart"] {
  if (!klantId) return null;
  for (const [, data] of qc.getQueriesData<V6Zaak>({ queryKey: v6Sleutels.zaken })) {
    // Waarop gekoppeld hoort bij die zaak, niet bij deze: weg.
    if (data && data.klant_id === klantId && data.klantkaart)
      return { ...data.klantkaart, gekoppeld_op: null };
  }
  return null;
}

export function Rechts() {
  const { zaakId, rechtsOpen, sluitRechts } = useV6();
  const smal = useSmal();
  const queryClient = useQueryClient();

  const zaak = useQuery({
    queryKey: v6Sleutels.zaak(zaakId ?? ""),
    queryFn: () => v6Api.zaak(zaakId as string),
    enabled: Boolean(zaakId),
    placeholderData: () => {
      const kaart = zaakId ? kaartUitCache(queryClient, zaakId) : null;
      return kaart
        ? { ...zaakUitKaart(kaart), klantkaart: klantkaartVanKlant(queryClient, kaart.klant_id) }
        : undefined;
    },
  });
  const zaakData = zaak.data?.zaak_id === zaakId ? zaak.data : undefined;

  // Escape sluit het vlak op de iPhone (toetsenbord of VoiceOver).
  useEffect(() => {
    if (!rechtsOpen) return;
    const toets = (e: KeyboardEvent) => {
      if (e.key === "Escape") sluitRechts();
    };
    document.addEventListener("keydown", toets);
    return () => document.removeEventListener("keydown", toets);
  }, [rechtsOpen, sluitRechts]);

  return (
    <>
      {rechtsOpen ? (
        <div className="v6-rechts-overlay" data-testid="rechts-overlay" onClick={sluitRechts} />
      ) : null}
      <aside
        className={`v6-rechts${rechtsOpen ? " toon" : ""}`}
        data-tab={rechtsOpen ?? undefined}
        aria-label="Klant en copiloot"
      >
        {/* Niet per zaak opnieuw (sessie 75): het vlak zet zijn bel- en
            koppelfouten zelf terug bij een andere zaak. */}
        <Klantvlak
          zaakId={zaakId}
          zaak={zaakData}
          zaakLaadt={zaak.isLoading}
          zaakVoorlopig={zaak.isPlaceholderData}
          zaakFout={zaak.isError}
          verborgen={smal && rechtsOpen !== "klant"}
        />
        {/* Wel per zaak opnieuw: de copiloot begint bij elke zaak leeg. */}
        <Copilootvlak
          key={`copiloot-${zaakId ?? "geen"}`}
          zaakId={zaakId}
          smal={smal}
          verborgen={smal && rechtsOpen !== "copiloot"}
        />
      </aside>
    </>
  );
}
